import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { BadgeCheck, Sparkles, MessageCircle } from 'lucide-react';
import { resolveMedia } from './discovery';
import { toast } from 'sonner';

const TABS = ['followers', 'following', 'friends'];

function UserRow({ u, onClose, onFollowChange }) {
  const [following, setFollowing] = useState(!!u.is_following);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    setLoading(true);
    try {
      if (following) {
        await api.delete(`/users/${u.id}/follow`);
        setFollowing(false);
        onFollowChange?.(u.id, false);
      } else {
        await api.post(`/users/${u.id}/follow`);
        setFollowing(true);
        onFollowChange?.(u.id, true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update follow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 py-2.5" data-testid="follow-list-row">
      <Link to={`/profile/${u.username}`} onClick={onClose} className="shrink-0">
        <Avatar className="w-11 h-11 border border-primary/30">
          <AvatarImage src={resolveMedia(u.avatar)} className="object-cover" />
          <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
        </Avatar>
      </Link>
      <Link to={`/profile/${u.username}`} onClick={onClose} className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="font-semibold truncate hover:underline">{u.display_name}</p>
          {u.is_verified && <BadgeCheck className="w-4 h-4 text-primary shrink-0" />}
          {u.is_npc && (
            <span className="mg-npc-badge"><Sparkles className="w-3 h-3" />Myth</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate">@{u.username}</p>
      </Link>
      {!u.is_self && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={toggle}
            disabled={loading}
            variant={following ? 'outline' : 'default'}
            className="rounded-full h-8 px-4 text-xs"
            data-testid="follow-list-follow-btn"
          >
            {following ? 'Following' : 'Follow'}
          </Button>
          <Link to={`/messages?user=${u.id}`} onClick={onClose}>
            <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" data-testid="follow-list-message-btn" aria-label="Message">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function FollowListModal({ open, onOpenChange, userId, username, initialTab = 'followers', counts = {}, onCountsChange }) {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState({ followers: null, following: null, friends: null });

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  const load = useCallback(async (which) => {
    if (!userId) return;
    try {
      const res = await api.get(`/users/${userId}/${which}`);
      setData(prev => ({ ...prev, [which]: res.data }));
    } catch (e) {
      setData(prev => ({ ...prev, [which]: [] }));
    }
  }, [userId]);

  useEffect(() => {
    if (open && data[tab] === null) load(tab);
  }, [open, tab, data, load]);

  // Reset cache whenever the dialog is (re)opened for a user
  useEffect(() => {
    if (open) setData({ followers: null, following: null, friends: null });
  }, [open, userId]);

  const label = (t) => `${t[0].toUpperCase()}${t.slice(1)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 bg-card border-primary/20" data-testid="follow-list-modal">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-lg">@{username}</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3 rounded-none bg-transparent border-b border-border p-0 h-auto">
            {TABS.map(t => (
              <TabsTrigger
                key={t}
                value={t}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary py-3"
                data-testid={`follow-tab-${t}`}
              >
                {label(t)}
                {counts[t] != null && <span className="ml-1.5 text-xs text-muted-foreground">{counts[t]}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(t => (
            <TabsContent key={t} value={t} className="mt-0">
              <div className="px-5 py-2 max-h-[55vh] overflow-y-auto" data-testid={`follow-list-${t}`}>
                {data[t] === null ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5">
                      <Skeleton className="w-11 h-11 rounded-full" />
                      <div className="flex-1 space-y-2"><Skeleton className="h-3 w-32" /><Skeleton className="h-3 w-20" /></div>
                    </div>
                  ))
                ) : data[t].length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-10">
                    {t === 'friends' ? 'No friends yet. Friends are people you both follow each other.' : `No ${t} yet.`}
                  </p>
                ) : (
                  data[t].map(u => (
                    <UserRow key={u.id} u={u} onClose={() => onOpenChange(false)} onFollowChange={onCountsChange} />
                  ))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
